import React, { useEffect, useMemo, useRef, useState } from 'react';
import DropdownArrow from '../components/DropdownArrow';
import { Container, Row, Col, Card, Alert, Modal, Button, Table, Form, Pagination } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { useNavigate } from 'react-router-dom';
import { accountHolderService, dashboardSettingsService, paymentService, trustAccountService } from '../services/apiClient';
import { notificationService } from '../services/notificationService';
import { AccountHolder, Payment, TrustAccount, TrustStatementPreviewResponse } from '../types/index';
import { useLocalization } from '../context/LocalizationContext';
import { DEFAULT_SYSTEM_CATEGORY_KEYWORDS, getSystemFeeCategoriesForAccountType, isSystemFeeCategoryName, normalizeKeyword, resolveSystemFeeCategory, resolveTransactionSystemCategory, SYSTEM_FEE_CATEGORIES } from '../utils/systemCategoryMapping';
import { getActiveSessionMonth, isSameMonth, setActiveSessionMonth, subscribeToActiveSessionMonth } from '../utils/sessionMonth';
import { getSavedTrustAccountId, isSavingsTrustAccount, saveSelectedTrustAccountId, sortTrustAccountsByKind } from '../utils/trustAccount';
import ConfirmationModal from '../components/ConfirmationModal';
import './Dashboard.css';

type AccountKeywordType = 'Current' | 'Savings';
type MonthlyComparisonPoint = {
  label: string;
  monthDate: Date;
  income: number;
  expenses: number;
};

type HolderAddressFeeBreakdown = {
  address: string;
  monthlyFee: number;
};

const isHolderTrackableForMonth = (holder: AccountHolder | undefined, month: Date): boolean => {
  if (!holder) return true;
  if (holder.isActive) return true;

  if (!holder.deactivatedDate) return false;
  const deactivated = new Date(holder.deactivatedDate);
  if (Number.isNaN(deactivated.getTime())) return false;

  const targetMonthStart = new Date(month.getFullYear(), month.getMonth(), 1).getTime();
  const deactivationMonthStart = new Date(deactivated.getFullYear(), deactivated.getMonth(), 1).getTime();
  return targetMonthStart < deactivationMonthStart;
};

const normalizeHolderAddressFees = (holder?: AccountHolder, forMonth?: Date): HolderAddressFeeBreakdown[] => {
  if (!holder) return [];

  if (forMonth && Array.isArray(holder.monthlyFeeHistory) && holder.monthlyFeeHistory.length > 0) {
    const targetMonth = new Date(forMonth.getFullYear(), forMonth.getMonth(), 1).getTime();
    const effectiveHistory = holder.monthlyFeeHistory
      .map((entry) => ({
        ...entry,
        effectiveFrom: new Date(entry.effectiveFromMonth),
        changedAt: new Date(entry.changedAt),
      }))
      .filter((entry) => !Number.isNaN(entry.effectiveFrom.getTime()))
      .sort((first, second) => {
        const effectiveDelta = first.effectiveFrom.getTime() - second.effectiveFrom.getTime();
        if (effectiveDelta !== 0) return effectiveDelta;
        return first.changedAt.getTime() - second.changedAt.getTime();
      })
      .filter((entry) => entry.effectiveFrom.getTime() <= targetMonth)
      .pop();

    if (effectiveHistory && Array.isArray(effectiveHistory.addressFees)) {
      const historyAddressFees = effectiveHistory.addressFees
        .map((item) => ({
          address: (item.address || '').trim(),
          monthlyFee: Number(item.monthlyFee) || 0,
        }))
        .filter((item) => !!item.address);

      if (historyAddressFees.length > 0) {
        return historyAddressFees;
      }
    }
  }

  const distributeMonthlyFee = (addresses: string[], totalMonthlyFee: number): HolderAddressFeeBreakdown[] => {
    if (addresses.length === 0) return [];
    const normalizedTotal = Math.max(0, Number(totalMonthlyFee) || 0);
    if (normalizedTotal <= 0) {
      return addresses.map((address) => ({ address, monthlyFee: 0 }));
    }

    const cents = Math.round(normalizedTotal * 100);
    const baseCents = Math.floor(cents / addresses.length);
    const remainder = cents % addresses.length;

    return addresses.map((address, index) => ({
      address,
      monthlyFee: (baseCents + (index < remainder ? 1 : 0)) / 100,
    }));
  };

  const normalizedFromAddressFees = (holder.addressFees || [])
    .map((item) => ({
      address: (item.address || '').trim(),
      monthlyFee: Number(item.monthlyFee) || 0,
    }))
    .filter((item) => !!item.address);

  if (normalizedFromAddressFees.length > 0) {
    const normalizedFromAddressFeesTotal = normalizedFromAddressFees.reduce((sum, item) => sum + item.monthlyFee, 0);
    const fallbackMonthlyFee = Number(holder.monthlyFee) || 0;

    if (normalizedFromAddressFeesTotal <= 0 && fallbackMonthlyFee > 0) {
      return distributeMonthlyFee(
        normalizedFromAddressFees.map((item) => item.address),
        fallbackMonthlyFee
      );
    }

    return normalizedFromAddressFees;
  }

  const fallbackAddresses = Array.from(new Set([
    ...(holder.addresses || []),
    holder.address || '',
  ].map((address) => (address || '').trim()).filter((address) => !!address)));

  const fallbackMonthlyFee = Number(holder.monthlyFee) || 0;
  return distributeMonthlyFee(fallbackAddresses, fallbackMonthlyFee);
};

export const Dashboard: React.FC = () => {
  const { t, formatCurrency, formatDate, language } = useLocalization();
  const navigate = useNavigate();

  const getApiErrorDetails = (error: unknown): string[] => {
    const rawErrors = (error as any)?.response?.data?.errors;
    if (!Array.isArray(rawErrors)) {
      return [];
    }

    return rawErrors
      .filter((entry) => typeof entry === 'string')
      .map((entry) => entry.trim())
      .filter((entry) => !!entry);
  };

  const getApiErrorMessage = (error: unknown, fallbackMessage: string) => {
    const responseMessage = (error as any)?.response?.data?.message;
    if (typeof responseMessage === 'string' && responseMessage.trim()) {
      const normalizedMessage = responseMessage.trim().toLowerCase();
      const isPdfExtractionFailure =
        normalizedMessage.includes('could not extract transactions from the uploaded pdf statement')
        || normalizedMessage.includes('no readable statement content was found in the uploaded pdf')
        || normalizedMessage.includes('pdf parsing is not available on the server');

      if (isPdfExtractionFailure) {
        return fallbackMessage;
      }

      return responseMessage;
    }

    const directMessage = (error as any)?.message;
    if (typeof directMessage === 'string' && directMessage.trim()) {
      return directMessage;
    }

    return fallbackMessage;
  };

  const getPreviousMonth = (): Date => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() - 1, 1);
  };

  const getMonthRange = (date: Date) => {
    const start = new Date(date.getFullYear(), date.getMonth(), 1);
    const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);

    return {
      start: new Date(`${start.toISOString().split('T')[0]}T00:00:00`).toISOString(),
      end: new Date(`${end.toISOString().split('T')[0]}T23:59:59`).toISOString(),
    };
  };

  const [selectedTrustAccount, setSelectedTrustAccount] = useState<TrustAccount | null>(null);
  const [trustAccounts, setTrustAccounts] = useState<TrustAccount[]>([]);
  const [trustTransactions, setTrustTransactions] = useState<Payment[]>([]);
  const [accountHolders, setAccountHolders] = useState<AccountHolder[]>([]);
  const [isMockMode, setIsMockMode] = useState(false);
  const [systemCategoryKeywordConfigByType, setSystemCategoryKeywordConfigByType] = useState<Record<AccountKeywordType, Record<string, string[]>>>({
    Current: {},
    Savings: {},
  });
  const [systemCategoryKeywordConfig, setSystemCategoryKeywordConfig] = useState<Record<string, string[]>>({});

  const [selectedMonth, setSelectedMonth] = useState<Date>(() => getActiveSessionMonth(getPreviousMonth()));
  const [filterAccountId, setFilterAccountId] = useState<number | undefined>();
  const [showSystemTransactions, setShowSystemTransactions] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [summaryCurrentPage, setSummaryCurrentPage] = useState(1);
  const [summaryPageSize, setSummaryPageSize] = useState(5);

  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [isTransactionsLoading, setIsTransactionsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [importing, setImporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const [showReviewModal, setShowReviewModal] = useState(false);
  const [importPreview, setImportPreview] = useState<TrustStatementPreviewResponse | null>(null);
  const [isStatementAccountNumberAutoPopulated, setIsStatementAccountNumberAutoPopulated] = useState(false);
  const [isStatementAccountNumberManualOverride, setIsStatementAccountNumberManualOverride] = useState(false);
  const [importFeedback, setImportFeedback] = useState<{ variant: 'success' | 'danger' | 'info'; message: string } | null>(null);
  const [reviewFeedback, setReviewFeedback] = useState<{ variant: 'success' | 'danger' | 'info'; message: string } | null>(null);
  const [showImportCloseConfirm, setShowImportCloseConfirm] = useState(false);
  const [importValidationErrors, setImportValidationErrors] = useState<string[]>([]);
  const [reviewValidationErrors, setReviewValidationErrors] = useState<string[]>([]);
  const [monthlyComparisonPoints, setMonthlyComparisonPoints] = useState<MonthlyComparisonPoint[]>([]);
  const [isMonthlyComparisonLoading, setIsMonthlyComparisonLoading] = useState(false);
  const [hoveredMonthlyBar, setHoveredMonthlyBar] = useState<{ index: number; series: 'income' | 'expenses' } | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const transactionCacheRef = useRef<Map<string, Payment[]>>(new Map());

  useEffect(() => {
    return subscribeToActiveSessionMonth((nextMonth) => {
      setSelectedMonth((previousMonth) => (isSameMonth(previousMonth, nextMonth) ? previousMonth : nextMonth));
    });
  }, []);

  useEffect(() => {
    setActiveSessionMonth(selectedMonth);
  }, [selectedMonth]);

  const normalizeSystemKeywordConfig = (source?: Record<string, string[]>, useDefaults = false) => {
    return SYSTEM_FEE_CATEGORIES.reduce<Record<string, string[]>>((accumulator, category) => {
      const defaults = DEFAULT_SYSTEM_CATEGORY_KEYWORDS[category] || [];
      const provided = source?.[category] || (useDefaults ? defaults : []);
      accumulator[category] = Array.from(new Set((provided || []).map(normalizeKeyword).filter(Boolean)));
      if (useDefaults && accumulator[category].length === 0) {
        accumulator[category] = Array.from(new Set(defaults.map(normalizeKeyword).filter(Boolean)));
      }
      return accumulator;
    }, {});
  };

  const normalizeAccountType = (kind?: string): AccountKeywordType => {
    return (kind || '').trim().toLowerCase() === 'savings' ? 'Savings' : 'Current';
  };

  const maskAccountNumber = (raw?: string) => {
    const digits = (raw || '').replace(/\D/g, '');
    if (!digits) return '**** **** ----';
    if (digits.length <= 4) return `**** **** ${digits}`;
    return `**** **** ${digits.slice(-4)}`;
  };

  const normalizeSystemKeywordConfigByType = (
    source?: Record<string, Record<string, string[]>>,
    legacy?: Record<string, string[]>,
    useDefaults = false
  ): Record<AccountKeywordType, Record<string, string[]>> => {
    const legacyNormalized = normalizeSystemKeywordConfig(legacy, useDefaults);

    const currentSource = source?.Current || source?.current || source?.Main || source?.main;
    const savingsSource = source?.Savings || source?.savings;

    const normalizeByCategories = (categories: string[], provided?: Record<string, string[]>, fallback?: Record<string, string[]>) => {
      return categories.reduce<Record<string, string[]>>((accumulator, category) => {
        const defaults = DEFAULT_SYSTEM_CATEGORY_KEYWORDS[category] || [];
        const fromProvided = provided?.[category];
        const fromFallback = fallback?.[category];
        const rawKeywords = fromProvided || fromFallback || (useDefaults ? defaults : []);

        accumulator[category] = Array.from(new Set((rawKeywords || []).map(normalizeKeyword).filter(Boolean)));
        if (useDefaults && accumulator[category].length === 0) {
          accumulator[category] = Array.from(new Set(defaults.map(normalizeKeyword).filter(Boolean)));
        }

        return accumulator;
      }, {});
    };

    return {
      Current: normalizeByCategories(getSystemFeeCategoriesForAccountType('Current'), currentSource || legacyNormalized),
      Savings: normalizeByCategories(getSystemFeeCategoriesForAccountType('Savings'), savingsSource),
    };
  };

  const filteredTransactions = useMemo(() => {
    let rows = trustTransactions;
    const isSavings = normalizeAccountType(selectedTrustAccount?.accountKind) === 'Savings';
    const holderById = new Map<number, AccountHolder>(accountHolders.map((holder) => [holder.id, holder]));

    if (!showSystemTransactions) {
      rows = rows.filter((tx) => {
        const category = resolveTransactionSystemCategory(tx, systemCategoryKeywordConfig);
        const effectiveCategory = isSavings && category?.trim().toLowerCase() === 'general' ? null : category;
        return !effectiveCategory;
      });
    }

    rows = rows.filter((tx) => {
      if (tx.accountHolderId == null) {
        return true;
      }

      return isHolderTrackableForMonth(holderById.get(tx.accountHolderId), selectedMonth);
    });

    if (!filterAccountId) return rows;
    return rows.filter((tx) => tx.accountHolderId === filterAccountId);
  }, [trustTransactions, filterAccountId, showSystemTransactions, systemCategoryKeywordConfig, selectedTrustAccount?.accountKind, accountHolders, selectedMonth]);

  const isSavingsSelected = useMemo(() => isSavingsTrustAccount(selectedTrustAccount), [selectedTrustAccount]);

  const monthlyComparison = useMemo(() => {
    const current = monthlyComparisonPoints[monthlyComparisonPoints.length - 1];
    const previous = monthlyComparisonPoints[monthlyComparisonPoints.length - 2];
    const chartMax = Math.max(500, Math.ceil(Math.max(1, ...monthlyComparisonPoints.map((item) => Math.max(item.income, item.expenses))) / 500) * 500);
    const incomeDelta = previous?.income ? ((current?.income || 0) - previous.income) / previous.income * 100 : 0;
    const expenseDelta = previous?.expenses ? ((current?.expenses || 0) - previous.expenses) / previous.expenses * 100 : 0;

    return {
      points: monthlyComparisonPoints,
      currentIncome: current?.income || 0,
      currentExpenses: current?.expenses || 0,
      incomeDelta,
      expenseDelta,
      chartMax,
      maxValue: Math.max(1, ...monthlyComparisonPoints.map((item) => Math.max(item.income, item.expenses))),
    };
  }, [monthlyComparisonPoints]);

  const monthlyChartPointCount = Math.max(1, monthlyComparison.points.length);
  const monthlyChartLeft = 28;
  const monthlyChartRight = 546;
  const monthlyChartTop = 22;
  const monthlyChartBottom = 186;
  const monthlyChartHeight = monthlyChartBottom - monthlyChartTop;
  const monthlyChartGroupWidth = (monthlyChartRight - monthlyChartLeft) / monthlyChartPointCount;

  const totalAccountBalance = useMemo(() => {
    return trustAccounts.reduce((sum, account) => sum + (account.balance || 0), 0);
  }, [trustAccounts]);

  const handleMonthlyBarClick = (monthDate: Date) => {
    setSelectedMonth(new Date(monthDate.getFullYear(), monthDate.getMonth(), 1));
    setCurrentPage(1);
  };

  const handleAccountBadgeNavigation = (account: TrustAccount) => {
    setSelectedTrustAccount(account);
    saveSelectedTrustAccountId(account.id, account.accountKind);
    setFilterAccountId(undefined);
    setCurrentPage(1);
    navigate('/payments');
  };

  const totalPages = Math.max(1, Math.ceil(filteredTransactions.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const pagedTransactions = useMemo(() => {
    const startIndex = (safeCurrentPage - 1) * pageSize;
    return filteredTransactions.slice(startIndex, startIndex + pageSize);
  }, [filteredTransactions, safeCurrentPage, pageSize]);

  const pageStart = filteredTransactions.length === 0 ? 0 : (safeCurrentPage - 1) * pageSize + 1;
  const pageEnd = Math.min(safeCurrentPage * pageSize, filteredTransactions.length);

  const accountHolderOverview = useMemo(() => {
    const holderById = new Map<number, AccountHolder>(
      accountHolders.map((holder) => [holder.id, holder])
    );
    const holderNameById = new Map<number, string>(
      accountHolders.map((holder) => [holder.id, (holder.name || '').trim()])
    );
    const holderMap = new Map<number, {
      holderId: number;
      name: string;
      transactionCount: number;
      incomingAmount: number;
      outgoingAmount: number;
      creditNotesTotal: number;
      creditNotesBreakdown: { label: string; amount: number }[];
      monthlyAccountFees: number;
      addressFees: HolderAddressFeeBreakdown[];
    }>();

    filteredTransactions.forEach((tx) => {
      // Only include mapped account holder transactions in this summary.
      if (tx.accountHolderId == null) {
        return;
      }

      if (!isHolderTrackableForMonth(holderById.get(tx.accountHolderId), selectedMonth)) {
        return;
      }

      // Exclude account fee/system-category rows from holder contribution summaries.
      const mappedSystemCategory = resolveTransactionSystemCategory(tx, systemCategoryKeywordConfig);
      if (mappedSystemCategory && isSystemFeeCategoryName(mappedSystemCategory)) {
        return;
      }

      const holderId = tx.accountHolderId;
      const holder = holderById.get(holderId);
      const holderAddressFees = normalizeHolderAddressFees(holderById.get(holderId), selectedMonth);
      const activeCreditNotes = (holder?.creditNotes || []).filter((note) => !note.isVoided);
      const activeCreditNotesTotal = activeCreditNotes.reduce((sum, note) => (
        note.isVoided ? sum : sum + (Number(note.amount) || 0)
      ), 0);
      const creditNotesBreakdown = activeCreditNotes.map((note, noteIndex) => ({
        label: ((note.description || '').trim()) || `Credit ${noteIndex + 1}`,
        amount: Number(note.amount) || 0,
      }));
      const resolvedName = (tx.accountHolderName || '').trim() || holderNameById.get(holderId) || `#${holderId}`;
      const existing = holderMap.get(holderId);

      if (existing) {
        existing.transactionCount += 1;
        if (tx.amount >= 0) {
          existing.incomingAmount += tx.amount;
        } else {
          existing.outgoingAmount += Math.abs(tx.amount);
        }
        return;
      }

      holderMap.set(holderId, {
        holderId,
        name: resolvedName,
        transactionCount: 1,
        incomingAmount: tx.amount >= 0 ? tx.amount : 0,
        outgoingAmount: tx.amount < 0 ? Math.abs(tx.amount) : 0,
        creditNotesTotal: activeCreditNotesTotal,
        creditNotesBreakdown,
        monthlyAccountFees: holderAddressFees.reduce((sum, item) => sum + item.monthlyFee, 0),
        addressFees: holderAddressFees,
      });
    });

    return Array.from(holderMap.values()).sort((a, b) => (b.incomingAmount + b.outgoingAmount) - (a.incomingAmount + a.outgoingAmount));
  }, [filteredTransactions, accountHolders, selectedMonth, systemCategoryKeywordConfig]);

  const summaryTotalPages = Math.max(1, Math.ceil(accountHolderOverview.length / summaryPageSize));
  const safeSummaryCurrentPage = Math.min(summaryCurrentPage, summaryTotalPages);
  const pagedAccountHolderOverview = useMemo(() => {
    const startIndex = (safeSummaryCurrentPage - 1) * summaryPageSize;
    return accountHolderOverview.slice(startIndex, startIndex + summaryPageSize);
  }, [accountHolderOverview, safeSummaryCurrentPage, summaryPageSize]);

  const summaryPageStart = accountHolderOverview.length === 0 ? 0 : (safeSummaryCurrentPage - 1) * summaryPageSize + 1;
  const summaryPageEnd = Math.min(safeSummaryCurrentPage * summaryPageSize, accountHolderOverview.length);

  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterAccountId, selectedMonth, pageSize, showSystemTransactions]);

  useEffect(() => {
    setSummaryCurrentPage(1);
  }, [filterAccountId, selectedMonth, showSystemTransactions, summaryPageSize]);

  useEffect(() => {
    if (!selectedTrustAccount) return;
    loadTrustTransactions(selectedTrustAccount.id, selectedMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedTrustAccount?.id]);

  useEffect(() => {
    const loadMonthlyComparison = async () => {
      if (!selectedTrustAccount) {
        setMonthlyComparisonPoints([]);
        return;
      }

      try {
        setIsMonthlyComparisonLoading(true);
        const months = Array.from({ length: 4 }, (_, index) => {
          const date = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() - (3 - index), 1);
          return {
            date,
            key: `${date.getFullYear()}-${date.getMonth() + 1}`,
            label: date.toLocaleString('default', { month: 'short' }),
          };
        });

        const totals = await Promise.all(months.map(async (month) => {
          const cacheKey = getTransactionCacheKey(selectedTrustAccount.id, month.date);
          let monthRows = transactionCacheRef.current.get(cacheKey);

          if (!monthRows) {
            const range = getMonthRange(month.date);
            const tx = await paymentService.getTrustTransactions(selectedTrustAccount.id, range.start, range.end);
            monthRows = [...tx].sort((a, b) => new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime());
            transactionCacheRef.current.set(cacheKey, monthRows);
          }

          const income = monthRows.filter((tx) => tx.amount > 0).reduce((sum, tx) => sum + tx.amount, 0);
          const expenses = monthRows.filter((tx) => tx.amount < 0).reduce((sum, tx) => sum + Math.abs(tx.amount), 0);

          return {
            label: month.label,
            monthDate: month.date,
            income,
            expenses,
          };
        }));

        setMonthlyComparisonPoints(totals);
      } catch (err) {
        console.error('Failed to load monthly comparison', err);
        setMonthlyComparisonPoints([]);
      } finally {
        setIsMonthlyComparisonLoading(false);
      }
    };

    void loadMonthlyComparison();
  }, [selectedTrustAccount, selectedMonth]);

  const apiGetTrustAccounts = async (): Promise<TrustAccount[]> => {
    try {
      return await trustAccountService.getAll();
    } catch (err) {
      console.error('Failed to load trust accounts', err);
      return [];
    }
  };

  const getTransactionCacheKey = (trustAccountId: number, forMonth: Date) => {
    return `${trustAccountId}:${forMonth.getFullYear()}-${forMonth.getMonth() + 1}`;
  };

  const handleTrustAccountSelection = async (nextAccount: TrustAccount | null) => {
    setSelectedTrustAccount(nextAccount);
    setFilterAccountId(undefined);
    setCurrentPage(1);

    if (nextAccount) {
      saveSelectedTrustAccountId(nextAccount.id, nextAccount.accountKind);
      await loadTrustTransactions(nextAccount.id, selectedMonth);
    } else {
      setTrustTransactions([]);
    }
  };

  const loadTrustTransactions = async (trustAccountId: number, forMonth: Date, forceRefresh = false) => {
    try {
      setIsTransactionsLoading(true);
      const cacheKey = getTransactionCacheKey(trustAccountId, forMonth);
      if (!forceRefresh) {
        const cached = transactionCacheRef.current.get(cacheKey);
        if (cached) {
          setTrustTransactions(cached);
          setIsTransactionsLoading(false);
          return;
        }
      }

      const range = getMonthRange(forMonth);
      const tx = await paymentService.getTrustTransactions(trustAccountId, range.start, range.end);
      const sorted = [...tx].sort((a, b) => new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime());
      transactionCacheRef.current.set(cacheKey, sorted);
      setTrustTransactions(sorted);
    } catch (err) {
      console.error('Failed to load trust transactions', err);
    } finally {
      setIsTransactionsLoading(false);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [trustAccounts, visibleHolders, dashboardSettings] = await Promise.all([
        apiGetTrustAccounts(),
        accountHolderService.getAll(),
        dashboardSettingsService.get(),
      ]);

      const orderedTrustAccounts = sortTrustAccountsByKind(trustAccounts);
      setTrustAccounts(orderedTrustAccounts);

      setPageSize(dashboardSettings.defaultPageSize);
      setShowSystemTransactions(dashboardSettings.defaultShowSystemTransactions);
      const settingsMockMode = dashboardSettings.isMockMode === true;
      setIsMockMode(settingsMockMode);
      const keywordConfigByType = normalizeSystemKeywordConfigByType(
        dashboardSettings.systemCategoryKeywordsByAccountType,
        dashboardSettings.systemCategoryKeywords,
        settingsMockMode
      );
      setSystemCategoryKeywordConfigByType(keywordConfigByType);
      setAccountHolders(visibleHolders);
      const savedTrustAccountId = getSavedTrustAccountId();
      const primaryTrust = (savedTrustAccountId
        ? orderedTrustAccounts.find((account) => account.id === savedTrustAccountId)
        : null) ?? orderedTrustAccounts[0] ?? null;
      setSelectedTrustAccount(primaryTrust);
      const primaryTrustType = normalizeAccountType(primaryTrust?.accountKind);
      setSystemCategoryKeywordConfig(keywordConfigByType[primaryTrustType]);
      if (primaryTrust) {
        saveSelectedTrustAccountId(primaryTrust.id, primaryTrust.accountKind);
      }

      if (primaryTrust) {
        await loadTrustTransactions(primaryTrust.id, selectedMonth);
      }
    } catch (err) {
      setError(t('failedToLoadDashboardData'));
      console.error(err);
    } finally {
      setLoading(false);
      setHasLoadedOnce(true);
    }
  };

  useEffect(() => {
    const selectedType = normalizeAccountType(selectedTrustAccount?.accountKind);
    setSystemCategoryKeywordConfig(systemCategoryKeywordConfigByType[selectedType] || (isMockMode ? DEFAULT_SYSTEM_CATEGORY_KEYWORDS : {}));
  }, [selectedTrustAccount?.accountKind, systemCategoryKeywordConfigByType, isMockMode]);

  const getReadyToSaveCount = (preview: TrustStatementPreviewResponse | null) => {
    if (!preview) return 0;
    return preview.transactions.filter((tx) => {
      if (!tx.isValid || !tx.isInStatementPeriod || tx.isIgnored) return false;
      if (tx.alreadyExists) return true;
      const hasSystemCategory = !!(
        (tx.accountHolderName && isSystemFeeCategoryName(tx.accountHolderName))
        || resolveSystemFeeCategory(tx.transactionDescription, systemCategoryKeywordConfig)
      );
      return isSavingsSelected ? hasSystemCategory : (!!tx.accountHolderId || hasSystemCategory);
    }).length;
  };

  const getRowsRequiringMappingCount = (preview: TrustStatementPreviewResponse | null) => {
    if (!preview) return 0;
    if (isSavingsSelected) {
      return 0;
    }

    return preview.transactions.filter((tx) => tx.isValid && tx.isInStatementPeriod && !tx.isIgnored && !tx.accountHolderId && !resolveSystemFeeCategory(tx.transactionDescription, systemCategoryKeywordConfig) && !tx.alreadyExists).length;
  };

  const getRowsToSkipCount = (preview: TrustStatementPreviewResponse | null) => {
    if (!preview) return 0;
    return preview.transactions.filter((tx) => {
      if (!tx.isValid || !tx.isInStatementPeriod || !tx.alreadyExists) return false;
      if (tx.isIgnored) return false;
      if (isSavingsSelected) return true;
      return !tx.accountHolderId || tx.accountHolderId === tx.existingAccountHolderId;
    }).length;
  };

  const getIgnoredRowsCount = (preview: TrustStatementPreviewResponse | null) => {
    if (!preview) return 0;
    return preview.transactions.filter((tx) => tx.isValid && tx.isInStatementPeriod && !!tx.isIgnored).length;
  };

  const normalizeAccountNumber = (raw: string) => raw.replace(/\D/g, '');

  const normalizeDescriptionSpacing = (value: string) => {
    if (!value) return '';

    return value
      .replace(/\s+/g, ' ')
      .replace(/([a-z]{2,})([A-Z][a-z])/g, '$1 $2')
      .replace(/([a-z]{2,})([A-Z]{2,})/g, '$1 $2')
      .replace(/\bBANKRC\b/gi, 'BANK RC')
      .trim();
  };

  const expandTenDigitCandidates = (normalized: string): string[] => {
    if (!normalized || normalized.length <= 10) return [];

    const candidates: string[] = [];
    const trailing = normalized.slice(-10);
    candidates.push(trailing);

    for (let index = 0; index <= normalized.length - 10; index += 1) {
      candidates.push(normalized.slice(index, index + 10));
    }

    return Array.from(new Set(candidates));
  };

  const resolvePreferredAccountNumber = (rawToken: string) => {
    const normalized = normalizeAccountNumber(rawToken);
    if (!normalized) return '';
    if (normalized.length === 10) return normalized;

    if (normalized.length > 10) {
      const expanded = expandTenDigitCandidates(normalized).filter((candidate) => !isLikelyDateLikeToken(rawToken, candidate));
      if (expanded.length > 0) return expanded[0];
    }

    return normalized.length >= 8 && normalized.length <= 16 ? normalized : '';
  };

  const isLikelyDateLikeToken = (rawToken: string, normalizedToken: string) => {
    const raw = rawToken.trim();

    if (/^\d{1,2}[-/.]\d{1,2}[-/.]\d{2,4}$/.test(raw)) return true;
    if (/^\d{4}[-/.]\d{1,2}[-/.]\d{1,2}$/.test(raw)) return true;
    if (/^\d{1,2}[-/.]\d{1,2}[-/.]\d{2,4}\s+\d{1,2}(?::\d{1,2}){0,2}$/.test(raw)) return true;
    if (/^\d{4}[-/.]\d{1,2}[-/.]\d{1,2}\s+\d{1,2}(?::\d{1,2}){0,2}$/.test(raw)) return true;
    if (normalizedToken.length === 10 && /\d{4}[-/.]\d{1,2}[-/.]\d{1,2}\s+\d{1,2}/.test(raw)) return true;

    if (normalizedToken.length === 8) {
      const year = Number(normalizedToken.slice(0, 4));
      const month = Number(normalizedToken.slice(4, 6));
      const day = Number(normalizedToken.slice(6, 8));

      if (year >= 1900 && year <= 2100) return true;
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31) return true;
    }

    return false;
  };

  const extractStatementAccountNumberFromText = (text: string) => {
    if (!text) return '';

    const normalizedText = text.split('\0').join(' ').replace(/\s+/g, ' ');
    const labeledPattern = /(?:current\s*account\s*(?:number|no)?|account\s*number|account\s*no|acc(?:ount)?\s*no|a\/c\s*(?:no|number)|acct\s*(?:no|number)|trust\s*account\s*(?:number|no)|rekening\s*(?:nommer|nr|no)|rek\s*no)\s*[:#-]?\s*([0-9][0-9\s./-]{6,}[0-9])/i;
    const labeledMatch = normalizedText.match(labeledPattern);
    if (labeledMatch?.[1]) {
      const candidate = resolvePreferredAccountNumber(labeledMatch[1]);
      if (candidate.length >= 8 && candidate.length <= 16) return candidate;
    }

    const genericMatches = normalizedText.match(/(?<!\d)\d[\d\s./-]{6,}\d(?!\d)/g) || [];
    const firstCandidate = genericMatches
      .map((token) => ({
        raw: token,
        normalized: resolvePreferredAccountNumber(token),
      }))
      .filter((candidate) => candidate.normalized.length >= 8 && candidate.normalized.length <= 16)
      .filter((candidate) => !isLikelyDateLikeToken(candidate.raw, candidate.normalized))
      .sort((a, b) => {
        const aDistance = Math.abs(a.normalized.length - 10);
        const bDistance = Math.abs(b.normalized.length - 10);
        if (aDistance !== bDistance) return aDistance - bDistance;
        return b.normalized.length - a.normalized.length;
      })
      .map((candidate) => candidate.normalized)[0];

    return firstCandidate || '';
  };

  const tryExtractStatementAccountNumberFromFile = async (file: File): Promise<string> => {
    try {
      const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      if (isPdf) {
        const buffer = await file.arrayBuffer();
        const binaryText = new TextDecoder('latin1').decode(buffer);
        return extractStatementAccountNumberFromText(binaryText);
      }

      const text = await file.text();
      return extractStatementAccountNumberFromText(text);
    } catch (err) {
      console.error('Failed to extract statement account number from file', err);
      return '';
    }
  };

  const handleFileImport = async (file: File) => {
    if (!file || !selectedTrustAccount) return;

    try {
      setImportFeedback(null);
      setReviewFeedback(null);
      setImportValidationErrors([]);
      setReviewValidationErrors([]);
      setIsStatementAccountNumberAutoPopulated(false);
      setIsStatementAccountNumberManualOverride(false);
      setPreviewing(true);

      const [preview, visibleHolders] = await Promise.all([
        paymentService.previewTrustStatementImport(selectedTrustAccount.id, file),
        accountHolderService.getAll(true),
      ]);

      let previewWithAccountNumber = preview;
      let accountNumberAutoPopulated = !!preview.statement?.trustAccountNumber?.trim();
      if (!accountNumberAutoPopulated) {
        const extractedAccountNumber = await tryExtractStatementAccountNumberFromFile(file);
        if (extractedAccountNumber) {
          accountNumberAutoPopulated = true;
          previewWithAccountNumber = {
            ...preview,
            statement: {
              ...preview.statement,
              trustAccountNumber: extractedAccountNumber,
            },
          };
        }
      }

      setAccountHolders(visibleHolders);
      setImportPreview(previewWithAccountNumber);
      setIsStatementAccountNumberAutoPopulated(accountNumberAutoPopulated);

      const mappedCount = previewWithAccountNumber.transactions.filter((tx) => tx.isValid && tx.isInStatementPeriod && !!tx.accountHolderId).length;
      const alreadyExistingCount = previewWithAccountNumber.transactions.filter((tx) => tx.isValid && tx.isInStatementPeriod && !!tx.alreadyExists).length;
      const categorizedCount = previewWithAccountNumber.transactions.filter((tx) => tx.isValid && tx.isInStatementPeriod && !!resolveSystemFeeCategory(tx.transactionDescription, systemCategoryKeywordConfig)).length;

      setReviewFeedback({
        variant: 'info',
        message: `${t('reviewExtractedRowsPrefix')} ${previewWithAccountNumber.totalRows} ${t('reviewExtractedRowsSuffix')} ${getReadyToSaveCount(previewWithAccountNumber)} ${t('transactionsReadyToSave')}.${!isSavingsSelected && mappedCount > 0 ? ` ${mappedCount} ${t('autoMappedByExtraction')}.` : ''}${isSavingsSelected && categorizedCount > 0 ? ` ${categorizedCount} ${t('autoMappedToSystemFeeCategory')}.` : ''}${alreadyExistingCount > 0 ? ` ${alreadyExistingCount} ${t('alreadyExistsMayBeSkippedOrUpdated')}.` : ''}`,
      });
      setReviewValidationErrors([]);
      setShowReviewModal(true);
    } catch (err) {
      console.error(err);
      setImportFeedback({
        variant: 'danger',
        message: getApiErrorMessage(err, t('couldNotExtractTransactionsFromFile')),
      });
      setImportValidationErrors(getApiErrorDetails(err));
    } finally {
      setPreviewing(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleManualMappingChange = (lineNumber: number, accountHolderIdValue: string) => {
    setImportPreview((previous) => {
      if (!previous) return previous;

      const previousTransaction = previous.transactions.find((tx) => tx.lineNumber === lineNumber);
      const hadMappedHolder = !!previousTransaction?.accountHolderId;
      const previousHolderName = previousTransaction?.accountHolderName || '';

      const parsedId = Number(accountHolderIdValue);
      const selectedHolder = accountHolders.find((holder) => holder.id === parsedId);
      const selectedIgnore = accountHolderIdValue === '__ignore__';

      const updatedTransactions = previous.transactions.map((tx) => {
        if (tx.lineNumber !== lineNumber) return tx;

        if (selectedIgnore) {
          return {
            ...tx,
            isIgnored: true,
            accountHolderId: undefined,
            accountHolderName: '',
          };
        }

        if (!selectedHolder) {
          return {
            ...tx,
            isIgnored: false,
            accountHolderId: undefined,
            accountHolderName: '',
          };
        }

        return {
          ...tx,
          isIgnored: false,
          accountHolderId: selectedHolder.id,
          accountHolderName: selectedHolder.name,
        };
      });

      const updatedPreview = {
        ...previous,
        transactions: updatedTransactions,
        readyToSaveRows: updatedTransactions.filter((tx) => tx.isValid && tx.isInStatementPeriod && !!tx.accountHolderId).length,
      };

      let mappingChangeMessage = '';
      if (selectedIgnore) {
        mappingChangeMessage = `${t('markedTransactionIgnoredOnSave')} `;
      } else if (hadMappedHolder && selectedHolder && previousTransaction?.accountHolderId !== selectedHolder.id) {
        mappingChangeMessage = `${t('updatedExistingMappingFrom')} ${previousHolderName || t('previousAccountHolder')} ${t('to')} ${selectedHolder.name}. `;
      } else if (hadMappedHolder && !selectedHolder) {
        mappingChangeMessage = `${t('removedExistingMappingForTransaction')} `;
      } else if (!hadMappedHolder && selectedHolder) {
        mappingChangeMessage = `${t('mappedTransactionTo')} ${selectedHolder.name}. `;
      }

      setReviewFeedback({
        variant: 'info',
        message: `${mappingChangeMessage}${updatedPreview.readyToSaveRows} ${t('transactionsReadyToSave')}. ${getRowsRequiringMappingCount(updatedPreview)} ${t('transactionsStillNeedAccountHolderMapping')}.`,
      });

      return updatedPreview;
    });
  };

  const handleSavingsCategoryMappingChange = (lineNumber: number, categoryValue: string) => {
    setImportPreview((previous) => {
      if (!previous) return previous;

      const previousTransaction = previous.transactions.find((tx) => tx.lineNumber === lineNumber);
      const previousCategory = previousTransaction?.accountHolderName || '';
      const selectedIgnore = categoryValue === '__ignore__';
      const selectedCategory = !selectedIgnore ? categoryValue.trim() : '';

      const updatedTransactions = previous.transactions.map((tx) => {
        if (tx.lineNumber !== lineNumber) return tx;

        if (selectedIgnore) {
          return {
            ...tx,
            isIgnored: true,
            accountHolderId: undefined,
            accountHolderName: '',
          };
        }

        if (!selectedCategory) {
          return {
            ...tx,
            isIgnored: false,
            accountHolderId: undefined,
            accountHolderName: '',
          };
        }

        return {
          ...tx,
          isIgnored: false,
          accountHolderId: undefined,
          accountHolderName: selectedCategory,
        };
      });

      const updatedPreview = {
        ...previous,
        transactions: updatedTransactions,
      };

      let mappingChangeMessage = '';
      if (selectedIgnore) {
        mappingChangeMessage = `${t('markedTransactionIgnoredOnSave')} `;
      } else if (!!previousCategory && selectedCategory && previousCategory !== selectedCategory) {
        mappingChangeMessage = `${t('updatedExistingMappingFrom')} ${previousCategory} ${t('to')} ${selectedCategory}. `;
      } else if (!!previousCategory && !selectedCategory) {
        mappingChangeMessage = `${t('removedExistingMappingForTransaction')} `;
      } else if (!previousCategory && !!selectedCategory) {
        mappingChangeMessage = `${t('mappedTransactionTo')} ${selectedCategory}. `;
      }

      setReviewFeedback({
        variant: 'info',
        message: `${mappingChangeMessage}${getReadyToSaveCount(updatedPreview)} ${t('transactionsReadyToSave')}.`,
      });

      return updatedPreview;
    });
  };

  const handleStatementAccountNumberChange = (value: string) => {
    setIsStatementAccountNumberAutoPopulated(false);
    setIsStatementAccountNumberManualOverride(false);
    setImportPreview((previous) => {
      if (!previous) return previous;

      return {
        ...previous,
        statement: {
          ...previous.statement,
          trustAccountNumber: value.replace(/\s+/g, '').trim(),
        },
      };
    });
  };

  const handleSaveReviewedImport = async () => {
    if (!selectedTrustAccount || !importPreview) return;

    try {
      setImporting(true);
      setReviewFeedback({ variant: 'info', message: t('savingReviewedTransactions') });
      setReviewValidationErrors([]);

      const result = await paymentService.saveTrustStatementImport(selectedTrustAccount.id, {
        periodStartDate: importPreview.statement.periodStartDate,
        periodEndDate: importPreview.statement.periodEndDate,
        statement: importPreview.statement,
        transactions: importPreview.transactions,
      });

      notificationService.success(result.message || t('importComplete'));
      setShowReviewModal(false);
      setImportPreview(null);
      setIsStatementAccountNumberAutoPopulated(false);
      setIsStatementAccountNumberManualOverride(false);
      setReviewFeedback(null);
      
      // Reload trust accounts to refresh dashboard heading with updated statement values
      const trustAccounts = sortTrustAccountsByKind(await apiGetTrustAccounts());
      setTrustAccounts(trustAccounts);
      const updated = trustAccounts.find(ta => ta.id === selectedTrustAccount.id);
      if (updated) {
        setSelectedTrustAccount(updated);
      }
      
      await loadTrustTransactions(selectedTrustAccount.id, selectedMonth, true);
    } catch (err) {
      console.error(err);
      const failureMessage = getApiErrorMessage(err, t('importFailed'));
      const failureDetails = getApiErrorDetails(err);
      setReviewFeedback({ variant: 'danger', message: failureMessage });
      setReviewValidationErrors(failureDetails);
      notificationService.error(failureMessage);
    } finally {
      setImporting(false);
    }
  };


  const isStatementAccountNumberAutoAvailable =
    isStatementAccountNumberAutoPopulated && !!importPreview?.statement?.trustAccountNumber?.trim();
  const isStatementAccountNumberLocked =
    isStatementAccountNumberAutoAvailable && !isStatementAccountNumberManualOverride;
  const isImportDisabled = previewing || importing || !selectedTrustAccount;

  if (loading && !hasLoadedOnce) {
    return (
      <div className="py-5">
        <div className="dashboard-header dashboard-loading-header">
          <Container>
            <div className="dashboard-loading-title-skeleton dashboard-skeleton-shimmer" aria-hidden="true" />
            <div className="mt-3 mb-0 dashboard-loading-text" role="status" aria-live="polite">
              <div className="dashboard-loading-status-card dashboard-loading-status-card-inline">
                <span className="dashboard-loading-dots" aria-hidden="true">
                  <span className="dashboard-loading-dot" />
                  <span className="dashboard-loading-dot" />
                  <span className="dashboard-loading-dot" />
                </span>
                <span className="visually-hidden">{t('loadingDashboard')}</span>
              </div>
            </div>
          </Container>
        </div>
        <Container>
          <Row className="g-4 mb-4">
            <Col lg={12}>
              <div className="dashboard-loading-card dashboard-skeleton-shimmer" aria-hidden="true" />
            </Col>
          </Row>
          <Row className="g-4 mb-4">
            <Col lg={12}>
              <div className="dashboard-loading-card dashboard-loading-card-tall dashboard-skeleton-shimmer" aria-hidden="true" />
            </Col>
          </Row>
          <Row className="g-4">
            <Col lg={6}>
              <div className="dashboard-loading-card dashboard-skeleton-shimmer" aria-hidden="true" />
            </Col>
            <Col lg={6}>
              <div className="dashboard-loading-card dashboard-skeleton-shimmer" aria-hidden="true" />
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div className="py-5" aria-busy={loading || isTransactionsLoading || previewing || importing}>
      <div className="dashboard-header">
        <Container>
          <div className="dashboard-hero-panel page-header-panel">
            <div className="dashboard-hero-header-row">
              <h1>
                <span className="page-header-title-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                    <path d="M4.5 19.5h15" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
                    <path d="M7.5 17V11.5M12 17V7.5M16.5 17V13" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
                    <circle cx="7.5" cy="10" r="1" fill="currentColor" />
                    <circle cx="12" cy="6" r="1" fill="currentColor" />
                    <circle cx="16.5" cy="11.5" r="1" fill="currentColor" />
                  </svg>
                </span>
                {t('dashboard')}
              </h1>
              <div className="dashboard-active-account-pill">
                <span className="dashboard-active-account-label">{t('activeAccount')}</span>
                <span className="dashboard-active-account-value">
                  {selectedTrustAccount?.accountName || normalizeAccountType(selectedTrustAccount?.accountKind)}
                  <span className="dashboard-active-account-type">{normalizeAccountType(selectedTrustAccount?.accountKind)}</span>
                </span>
              </div>
            </div>
          </div>
        </Container>
      </div>

      <Container>
        {error && <Alert variant="danger" className="mt-3">⚠️ {error}</Alert>}

        <Row className="g-4 mb-4">
          <Col lg={12}>
            <Card className="dashboard-card dashboard-wire-main-card">
              <Card.Body className="dashboard-main-panel dashboard-wire-main-panel">
                <section className="dashboard-main-column dashboard-main-balance-panel dashboard-wire-balance-panel">
                  <div className="dashboard-main-panel-header dashboard-wire-panel-header">
                    <Card.Title>{t('totalBalance')}</Card.Title>
                    <span className="dashboard-main-panel-chip">{t('allAccounts')}</span>
                  </div>
                  <div className="dashboard-main-balance-total-value">{formatCurrency(totalAccountBalance)}</div>
                  <div className="dashboard-main-balance-subtitle">{trustAccounts.length} {t('trackedAccounts')}</div>
                  <div className="dashboard-main-account-badges">
                    {trustAccounts.map((account) => {
                      const accountType = normalizeAccountType(account.accountKind);
                      return (
                        <button
                          key={`account-badge-${account.id}`}
                          type="button"
                          className="dashboard-main-account-badge"
                          onClick={() => handleAccountBadgeNavigation(account)}
                        >
                          <div className="dashboard-main-account-badge-info">
                            <div className="dashboard-main-account-badge-kicker">{t('accountType')}</div>
                            <div className="dashboard-main-account-badge-name">{accountType}</div>
                            <div className="dashboard-main-account-badge-meta">{maskAccountNumber(account.lastStatementAccountNumber || account.accountNumber)}</div>
                          </div>
                          <div className="dashboard-main-account-badge-side">
                            <div className="dashboard-main-account-badge-value">{formatCurrency(account.balance || 0)}</div>
                            <span className="dashboard-main-account-badge-arrow" aria-hidden="true">
                              <svg viewBox="0 0 16 16" className="dashboard-main-account-badge-arrow-icon" focusable="false" aria-hidden="true">
                                <path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </section>

                <section className="dashboard-main-column dashboard-main-comparison-panel dashboard-wire-comparison-panel">
                  <div className="dashboard-main-panel-header dashboard-wire-panel-header">
                    <Card.Title>{t('monthlyComparison')}</Card.Title>
                  </div>
                  <div className="dashboard-main-account-select-wrap app-dropdown-arrow-wrapper">
                    <Form.Select
                      value={selectedTrustAccount?.id || ''}
                      onChange={async (e) => {
                        const accountId = Number(e.target.value);
                        const nextAccount = trustAccounts.find((account) => account.id === accountId) || null;
                        await handleTrustAccountSelection(nextAccount);
                      }}
                    >
                      {trustAccounts.map((account) => (
                        <option key={account.id} value={account.id}>{account.accountName}</option>
                      ))}
                    </Form.Select>
                    <DropdownArrow />
                  </div>

                  <div className="dashboard-monthly-comparison-card dashboard-wire-chart-shell">
                    {isMonthlyComparisonLoading ? (
                      <div className="dashboard-monthly-chart-loading" role="status" aria-live="polite">
                        <div className="dashboard-loading-status-card dashboard-loading-status-card-subtle">
                          <span className="dashboard-loading-dots" aria-hidden="true">
                            <span className="dashboard-loading-dot" />
                            <span className="dashboard-loading-dot" />
                            <span className="dashboard-loading-dot" />
                          </span>
                          <span className="dashboard-loading-line dashboard-skeleton-shimmer" aria-hidden="true" />
                          <span className="dashboard-loading-line dashboard-loading-line-short dashboard-skeleton-shimmer" aria-hidden="true" />
                          <span className="visually-hidden">{t('loadingDashboard')}</span>
                        </div>
                      </div>
                    ) : (
                      <svg viewBox="0 0 560 230" className="dashboard-monthly-chart" role="img" aria-label="Monthly income and expenses chart">
                        {[0, 1, 2, 3, 4, 5].map((tick) => {
                          const y = monthlyChartBottom - (tick / 5) * monthlyChartHeight;
                          const value = Math.round((monthlyComparison.chartMax * (5 - tick)) / 5);
                          return (
                            <g key={`bar-grid-${tick}`}>
                              <line x1={monthlyChartLeft} y1={y} x2={monthlyChartRight} y2={y} className="dashboard-monthly-chart-grid" />
                              <text x={monthlyChartLeft - 8} y={y + 4} textAnchor="end" className="dashboard-monthly-chart-axis-label">{value}</text>
                            </g>
                          );
                        })}
                        <line x1={monthlyChartLeft} y1={monthlyChartBottom} x2={monthlyChartRight} y2={monthlyChartBottom} className="dashboard-monthly-chart-axis" />
                        {monthlyComparison.points.map((point, index) => {
                          const groupX = monthlyChartLeft + monthlyChartGroupWidth * (index + 0.5);
                          const incomeHeight = (point.income / monthlyComparison.chartMax) * monthlyChartHeight;
                          const expenseHeight = (point.expenses / monthlyComparison.chartMax) * monthlyChartHeight;
                          const isIncomeHovered = hoveredMonthlyBar?.index === index && hoveredMonthlyBar.series === 'income';
                          const isExpenseHovered = hoveredMonthlyBar?.index === index && hoveredMonthlyBar.series === 'expenses';
                          const isSelectedMonthPoint =
                            point.monthDate.getFullYear() === selectedMonth.getFullYear()
                            && point.monthDate.getMonth() === selectedMonth.getMonth();
                          const incomeX = groupX - 17;
                          const expenseX = groupX + 3;
                          const incomeY = monthlyChartBottom - incomeHeight;
                          const expenseY = monthlyChartBottom - expenseHeight;
                          const incomeHoverLift = isIncomeHovered ? 4 : 0;
                          const expenseHoverLift = isExpenseHovered ? 4 : 0;

                          return (
                            <g key={`bar-point-${point.label}-${index}`}>
                              <rect
                                x={incomeX}
                                y={incomeY - incomeHoverLift}
                                width="16"
                                height={incomeHeight + incomeHoverLift}
                                rx="3"
                                className={`dashboard-monthly-chart-income-bar dashboard-monthly-chart-bar ${isIncomeHovered ? 'is-hovered' : ''} ${isSelectedMonthPoint ? 'is-selected' : ''}`}
                                onMouseEnter={() => setHoveredMonthlyBar({ index, series: 'income' })}
                                onMouseLeave={() => setHoveredMonthlyBar(null)}
                                onClick={() => handleMonthlyBarClick(point.monthDate)}
                              />
                              <rect
                                x={expenseX}
                                y={expenseY - expenseHoverLift}
                                width="16"
                                height={expenseHeight + expenseHoverLift}
                                rx="3"
                                className={`dashboard-monthly-chart-expense-bar dashboard-monthly-chart-bar ${isExpenseHovered ? 'is-hovered' : ''} ${isSelectedMonthPoint ? 'is-selected' : ''}`}
                                onMouseEnter={() => setHoveredMonthlyBar({ index, series: 'expenses' })}
                                onMouseLeave={() => setHoveredMonthlyBar(null)}
                                onClick={() => handleMonthlyBarClick(point.monthDate)}
                              />
                              {isIncomeHovered && (
                                <text x={incomeX + 8} y={Math.max(monthlyChartTop - 2, incomeY - 9)} textAnchor="middle" className="dashboard-monthly-chart-tooltip income">
                                  {formatCurrency(point.income)}
                                </text>
                              )}
                              {isExpenseHovered && (
                                <text x={expenseX + 8} y={Math.max(monthlyChartTop - 2, expenseY - 9)} textAnchor="middle" className="dashboard-monthly-chart-tooltip expenses">
                                  {formatCurrency(point.expenses)}
                                </text>
                              )}
                              <text x={groupX} y={monthlyChartBottom + 21} textAnchor="middle" className="dashboard-monthly-chart-label">{point.label}</text>
                            </g>
                          );
                        })}
                        <rect x="404" y="14" width="8" height="8" className="dashboard-monthly-chart-income-bar" rx="1" />
                        <text x="416" y="22" className="dashboard-monthly-chart-legend income">Income</text>
                        <rect x="470" y="14" width="8" height="8" className="dashboard-monthly-chart-expense-bar" rx="1" />
                        <text x="482" y="22" className="dashboard-monthly-chart-legend expenses">Expenses</text>
                      </svg>
                    )}
                  </div>
                </section>

                <section className="dashboard-main-column dashboard-main-totals-panel dashboard-wire-kpi-stack">
                  <div className="dashboard-side-total-card dashboard-side-total-card-income dashboard-wire-kpi-card">
                    <div className="dashboard-side-total-label-row">
                      <div className="dashboard-side-total-label">{t('totalIncome')}</div>
                      <span className="dashboard-side-total-dot income" aria-hidden="true" />
                    </div>
                    <div className="dashboard-side-total-value">{formatCurrency(monthlyComparison.currentIncome)}</div>
                    <div className="dashboard-side-total-delta">{monthlyComparison.incomeDelta >= 0 ? '+' : ''}{monthlyComparison.incomeDelta.toFixed(1)} %</div>
                    <div className="dashboard-side-total-caption">vs previous month</div>
                  </div>
                  <div className="dashboard-side-total-card dashboard-side-total-card-expense dashboard-wire-kpi-card">
                    <div className="dashboard-side-total-label-row">
                      <div className="dashboard-side-total-label">{t('totalExpenses')}</div>
                      <span className="dashboard-side-total-dot expense" aria-hidden="true" />
                    </div>
                    <div className="dashboard-side-total-value">{formatCurrency(monthlyComparison.currentExpenses)}</div>
                    <div className="dashboard-side-total-delta">{monthlyComparison.expenseDelta >= 0 ? '+' : ''}{monthlyComparison.expenseDelta.toFixed(1)} %</div>
                    <div className="dashboard-side-total-caption">vs previous month</div>
                  </div>
                </section>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-4 mb-4">
          <Col lg={12}>
            <Card className="dashboard-card dashboard-recent-card dashboard-wire-transactions-card d-flex flex-column">
              <Card.Body className="d-flex flex-column dashboard-wire-transactions-body">
                <div className="dashboard-recent-header mb-3">
                  <div>
                    <Card.Title className="mb-0">{t('recentTransactions')}</Card.Title>
                  </div>
                </div>

                <div className="dashboard-recent-control-strip mb-3" style={{ gap: '1.2rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  <input
                    type="file"
                    accept=".pdf"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file || !selectedTrustAccount) return;
                      await handleFileImport(file);
                    }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', minWidth: 180 }}>
                    <div className="dashboard-recent-utility-group">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="dashboard-wire-action-btn dashboard-recent-utility-btn"
                        onClick={() => {
                          if (isImportDisabled) return;
                          fileInputRef.current?.click();
                        }}
                        disabled={isImportDisabled}
                      >
                        {previewing ? t('extractingTransactions') : importing ? t('savingTransactions') : t('importStatementDropzone')}
                      </Button>
                    </div>
                  </div>
                  <div className="dashboard-recent-filter-line" style={{ flex: 1, minWidth: 220, gap: '1.2rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    <div className="dashboard-recent-inline-filters" style={{ flexWrap: 'wrap', gap: '0.65rem' }}>
                      {!isSavingsSelected && (
                        <div className="app-dropdown-arrow-wrapper">
                          <Form.Select
                            className="dashboard-recent-inline-select"
                            value={filterAccountId || ''}
                            onChange={(e) => setFilterAccountId(e.target.value ? parseInt(e.target.value, 10) : undefined)}
                            disabled={isTransactionsLoading}
                          >
                            <option value="">{t('allAccountHolders')}</option>
                            {accountHolders.map((holder) => (
                              <option key={holder.id} value={holder.id}>
                                {holder.name}
                              </option>
                            ))}
                          </Form.Select>
                          <DropdownArrow />
                        </div>
                      )}
                      <div className="app-date-picker-shell dashboard-recent-inline-month-shell">
                        <DatePicker
                          selected={selectedMonth}
                          onChange={(date: Date | null) => date && setSelectedMonth(date)}
                          onChangeRaw={(e) => e.preventDefault()}
                          onKeyDown={(e) => e.preventDefault()}
                          dateFormat="MMMM yyyy"
                          showMonthYearPicker
                          showFullMonthYearPicker
                          locale={language === 'af' ? 'af' : 'en-GB'}
                          className="form-control app-month-picker"
                          placeholderText={t('selectMonth')}
                          disabled={isTransactionsLoading}
                        />
                        <span className="app-date-picker-icon" aria-hidden="true">
                          <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                            <rect x="3.5" y="5.5" width="17" height="15" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
                            <path d="M7.5 3.8v3.4M16.5 3.8v3.4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                            <path d="M3.8 9.2h16.4" fill="none" stroke="currentColor" strokeWidth="1.8" />
                            <path d="M8.2 12.8h3.1M8.2 16h5.8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                          </svg>
                        </span>
                      </div>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="dashboard-wire-action-btn dashboard-recent-utility-btn dashboard-recent-reset-btn"
                        onClick={() => {
                          setFilterAccountId(undefined);
                          setSelectedMonth(getPreviousMonth());
                          setShowSystemTransactions(true);
                        }}
                        disabled={isTransactionsLoading}
                      >
                        {t('reset')}
                      </Button>
                    </div>
                    <div className="dashboard-recent-fees-toggle-wrap">
                      <Form.Check
                        type="switch"
                        id="show-system-transactions"
                        className="dashboard-recent-fees-toggle"
                        label={t('showSystemFeesChargesInTransactions')}
                        checked={showSystemTransactions}
                        onChange={(e) => setShowSystemTransactions(e.target.checked)}
                        disabled={isTransactionsLoading}
                      />
                    </div>
                  </div>
                </div>

                {importFeedback && (
                  <Alert variant={importFeedback.variant} className="mb-3 dashboard-import-feedback-alert">
                    <div>{importFeedback.message}</div>
                    {importFeedback.variant === 'danger' && importValidationErrors.length > 0 && (
                      <details className="mt-2">
                        <summary>{t('validationDetails')} ({importValidationErrors.length})</summary>
                        <ul className="mb-0 mt-2 ps-3">
                          {importValidationErrors.map((validationError, index) => (
                            <li key={`${validationError}-${index}`}>{validationError}</li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </Alert>
                )}

                {filteredTransactions.length === 0 && !isTransactionsLoading ? (
                  <div className="info-box info flex-grow-1 d-flex align-items-center justify-content-center">
                    ℹ️ {t('noTrustAccountTransactionsForFilters')}
                  </div>
                ) : (
                  <>
                    <div className="scrollable-table dashboard-wire-table-wrap flex-grow-1 mb-3">
                      <table className="table dashboard-table">
                        <thead>
                          <tr>
                            <th>{t('date')}</th>
                            <th className="d-none d-md-table-cell">{t('description')}</th>
                            <th className="d-none d-lg-table-cell">{t('accountHolder')}</th>
                            <th className="text-end">{t('amount')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {isTransactionsLoading ? (
                            <tr>
                              <td colSpan={4} className="text-center py-4 text-muted">
                                <div className="dashboard-grid-loading-message">
                                  <div className="dashboard-loading-status-card" role="status" aria-live="polite">
                                    <span className="dashboard-loading-dots" aria-hidden="true">
                                      <span className="dashboard-loading-dot" />
                                      <span className="dashboard-loading-dot" />
                                      <span className="dashboard-loading-dot" />
                                    </span>
                                    <span className="dashboard-loading-line dashboard-skeleton-shimmer" aria-hidden="true" />
                                    <span className="dashboard-loading-line dashboard-loading-line-short dashboard-skeleton-shimmer" aria-hidden="true" />
                                    <span className="visually-hidden">{t('loadingDashboard')}</span>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ) : pagedTransactions.map((tx) => (
                            <tr key={tx.id}>
                              <td>{formatDate(tx.paymentDate)}</td>
                              <td className="d-none d-md-table-cell text-truncate" title={tx.description}>{tx.description}</td>
                              <td className="d-none d-lg-table-cell">{tx.accountHolderName}</td>
                              <td className={`text-end ${tx.amount > 0 ? 'amount-positive' : 'amount-negative'}`}>{formatCurrency(tx.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="dashboard-table-footer d-flex justify-content-between align-items-center">
                      <div className="dashboard-table-meta text-muted">{t('showing')} {pageStart}-{pageEnd} {t('of')} {filteredTransactions.length}</div>
                      <div className="d-flex align-items-center gap-2">
                        <Form.Label className="mb-0 dashboard-rows-label">{t('rows')}</Form.Label>
                        <div className="app-dropdown-arrow-wrapper" style={{ width: '90px' }}>
                          <Form.Select
                            className="dashboard-rows-select"
                            value={pageSize}
                            onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
                            disabled={isTransactionsLoading}
                          >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                          </Form.Select>
                          <DropdownArrow />
                        </div>
                        <Pagination className="mb-0 dashboard-pagination">
                          <Pagination.First onClick={() => setCurrentPage(1)} disabled={safeCurrentPage === 1 || isTransactionsLoading} />
                          <Pagination.Prev onClick={() => setCurrentPage((page) => Math.max(1, page - 1))} disabled={safeCurrentPage === 1 || isTransactionsLoading} />
                          <Pagination.Item active>{safeCurrentPage}</Pagination.Item>
                          {totalPages > 1 && <Pagination.Item disabled>/ {totalPages}</Pagination.Item>}
                          <Pagination.Next onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))} disabled={safeCurrentPage === totalPages || isTransactionsLoading} />
                          <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={safeCurrentPage === totalPages || isTransactionsLoading} />
                        </Pagination>
                      </div>
                    </div>

                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-4 mb-4">
          <Col lg={12}>
            <Card className="dashboard-card dashboard-account-holder-card">
              <Card.Body className="p-0">
                <section className="dashboard-account-holder-overview dashboard-account-holder-overview-standalone standard-grid-shell" aria-label={t('accountHolderSummary')}>
                  <div className="dashboard-account-holder-overview-header">
                    <div>
                      <h6 className="mb-0">{t('accountHolderSummary')}</h6>
                      <div className="dashboard-account-holder-overview-subtitle">{t('accountHolderSummarySubtitle')}</div>
                    </div>
                    <span className="dashboard-account-holder-overview-count">{accountHolderOverview.length} {t('accountHolder')}</span>
                  </div>

                  {accountHolderOverview.length === 0 ? (
                    <div className="dashboard-account-holder-overview-empty text-muted">{t('noAccountHolderSummaryForFilters')}</div>
                  ) : (
                    <div className="dashboard-account-holder-overview-list">
                      {pagedAccountHolderOverview.map((holder, index) => (
                        <div key={`${holder.name}-${index}`} className="dashboard-account-holder-overview-row">
                          <div>
                            <div className="dashboard-account-holder-overview-name">{holder.name}</div>
                            <div className="dashboard-account-holder-overview-meta">{holder.transactionCount} {t('transactions')}</div>
                            <div className="dashboard-account-holder-overview-meta">{t('activeCreditNotesTotal')}: {formatCurrency(holder.creditNotesTotal)}</div>
                            {holder.creditNotesBreakdown.length > 0 && (
                              <div className="dashboard-account-holder-overview-meta text-muted">
                                {t('creditNotes')}: {holder.creditNotesBreakdown.map((note) => `${note.label} (${formatCurrency(note.amount)})`).join(' • ')}
                              </div>
                            )}
                            <div className="dashboard-account-holder-overview-meta">{t('monthlyAccountFees')}: {formatCurrency(holder.monthlyAccountFees)}</div>
                            {holder.addressFees.length > 0 && (
                              <div className="dashboard-account-holder-overview-meta text-muted">
                                {t('accountFeeBreakdown')}: {holder.addressFees.map((item) => `${item.address} (${formatCurrency(item.monthlyFee)})`).join(' • ')}
                              </div>
                            )}
                          </div>
                          <div className="dashboard-account-holder-overview-amounts">
                            <div className="dashboard-account-holder-overview-incoming dashboard-account-holder-overview-metric-chip">
                              <span className="dashboard-account-holder-overview-amount-label">{t('incoming')}</span>
                              <span>{formatCurrency(holder.incomingAmount)}</span>
                            </div>
                            <div className="dashboard-account-holder-overview-outgoing dashboard-account-holder-overview-metric-chip">
                              <span className="dashboard-account-holder-overview-amount-label">{t('outgoing')}</span>
                              <span>{formatCurrency(holder.outgoingAmount)}</span>
                            </div>
                            <div className="dashboard-account-holder-overview-net dashboard-account-holder-overview-metric-chip">
                              <span className="dashboard-account-holder-overview-amount-label">Net</span>
                              <span className={holder.incomingAmount - holder.outgoingAmount >= 0 ? 'amount-positive' : 'amount-negative'}>
                                {formatCurrency(holder.incomingAmount - holder.outgoingAmount)}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}

                      <div className="dashboard-table-footer d-flex justify-content-between align-items-center dashboard-account-holder-overview-footer">
                        <div className="dashboard-table-meta text-muted">{t('showing')} {summaryPageStart}-{summaryPageEnd} {t('of')} {accountHolderOverview.length}</div>
                        <div className="d-flex align-items-center gap-2">
                          <Form.Label className="mb-0 dashboard-rows-label">{t('rows')}</Form.Label>
                          <div className="app-dropdown-arrow-wrapper" style={{ width: '90px' }}>
                            <Form.Select
                              className="dashboard-rows-select"
                              value={summaryPageSize}
                              onChange={(e) => setSummaryPageSize(parseInt(e.target.value, 10))}
                            >
                              <option value={5}>5</option>
                              <option value={10}>10</option>
                              <option value={25}>25</option>
                            </Form.Select>
                            <DropdownArrow />
                          </div>
                          <Pagination className="mb-0 dashboard-pagination">
                            <Pagination.First onClick={() => setSummaryCurrentPage(1)} disabled={safeSummaryCurrentPage === 1} />
                            <Pagination.Prev onClick={() => setSummaryCurrentPage((page) => Math.max(1, page - 1))} disabled={safeSummaryCurrentPage === 1} />
                            <Pagination.Item active>{safeSummaryCurrentPage}</Pagination.Item>
                            {summaryTotalPages > 1 && <Pagination.Item disabled>/ {summaryTotalPages}</Pagination.Item>}
                            <Pagination.Next onClick={() => setSummaryCurrentPage((page) => Math.min(summaryTotalPages, page + 1))} disabled={safeSummaryCurrentPage === summaryTotalPages} />
                            <Pagination.Last onClick={() => setSummaryCurrentPage(summaryTotalPages)} disabled={safeSummaryCurrentPage === summaryTotalPages} />
                          </Pagination>
                        </div>
                      </div>
                    </div>
                  )}
                </section>
              </Card.Body>
            </Card>
          </Col>
        </Row>

      </Container>

      <Modal
        show={showReviewModal}
        onHide={() => {
          if (importing) return;
          if (importPreview) {
            setShowImportCloseConfirm(true);
          } else {
            setShowReviewModal(false);
            setIsStatementAccountNumberAutoPopulated(false);
            setIsStatementAccountNumberManualOverride(false);
            setReviewFeedback(null);
          }
        }}
        size="xl"
        centered
        className={`transaction-review-modal ${importing ? 'is-processing' : ''}`}
      >
        <Modal.Header closeButton={!importing} className={importing ? 'review-modal-header-processing' : ''}>
          <Modal.Title>
            <div className="d-flex flex-column review-modal-title-wrap">
              <span className="review-modal-title-main">
                {importing && (
                  <span className="dashboard-loading-dots dashboard-loading-dots-compact" aria-hidden="true">
                    <span className="dashboard-loading-dot" />
                    <span className="dashboard-loading-dot" />
                    <span className="dashboard-loading-dot" />
                  </span>
                )}
                {importing ? t('savingTransactions') : t('reviewExtractedTransactions')}
              </span>
              <small className="review-modal-subtitle">{t('verifyTransactionMappingsBeforeSaving')}</small>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={importing ? 'review-modal-body review-modal-body-processing' : 'review-modal-body'} aria-busy={importing}>
          {!importPreview ? (
            <p className="mb-0">{t('noPreviewDataAvailable')}</p>
          ) : (
            <>
              {importing && (
                <Alert variant="info" className="review-modal-processing-alert mb-3" role="status" aria-live="polite">
                  <div className="review-modal-processing-alert-content">
                    <span className="dashboard-loading-dots dashboard-loading-dots-compact review-modal-processing-alert-loader" aria-hidden="true">
                      <span className="dashboard-loading-dot" />
                      <span className="dashboard-loading-dot" />
                      <span className="dashboard-loading-dot" />
                    </span>
                    <div className="review-modal-processing-alert-text">
                      <div className="review-modal-processing-alert-title">{t('savingTransactions')}</div>
                      <div className="review-modal-processing-alert-subtitle">{t('statementSavingInProgressHint')}</div>
                      <div className="review-modal-processing-alert-note">{t('reviewModalSavingVisibilityHint')}</div>
                    </div>
                  </div>
                </Alert>
              )}

              {reviewFeedback && (
                <Alert variant={reviewFeedback.variant} className="mb-3">
                  <div>{reviewFeedback.message}</div>
                  {reviewFeedback.variant === 'danger' && reviewValidationErrors.length > 0 && (
                    <details className="mt-2">
                      <summary>{t('validationDetails')} ({reviewValidationErrors.length})</summary>
                      <ul className="mb-0 mt-2 ps-3">
                        {reviewValidationErrors.map((validationError, index) => (
                          <li key={`${validationError}-${index}`}>{validationError}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </Alert>
              )}

              <div className="dashboard-filter-panel border rounded p-3 mb-3 review-overview-panel">
                <div className="review-overview-header">
                  <div className="review-overview-title">{t('extractionOverviewTitle')}</div>
                  <small className="review-overview-subtitle">{t('extractionOverviewSubtitle')}</small>
                </div>
                <Row className="g-3">
                  <Col md={6} lg={5} className="review-account-number-col">
                    <div className="d-flex align-items-center justify-content-between gap-2 review-account-number-header">
                      <Form.Label className="mb-0">{t('statementAccountNumberLabel')}</Form.Label>
                      {isStatementAccountNumberAutoAvailable && (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="review-account-toggle-btn"
                          disabled={importing}
                          onClick={() => setIsStatementAccountNumberManualOverride((previous) => !previous)}
                        >
                          {isStatementAccountNumberLocked ? t('statementAccountNumberEditAction') : t('statementAccountNumberLockAction')}
                        </Button>
                      )}
                    </div>
                    <Form.Control
                      type="text"
                      inputMode="numeric"
                      placeholder={t('enterStatementAccountNumber')}
                      readOnly={isStatementAccountNumberLocked}
                      className={isStatementAccountNumberLocked ? 'review-account-number-input is-readonly' : 'review-account-number-input'}
                      value={importPreview.statement.trustAccountNumber || ''}
                      onChange={(e) => handleStatementAccountNumberChange(e.target.value)}
                    />
                    <small className="text-muted d-block mt-1 review-account-number-helper">
                      {isStatementAccountNumberLocked ? t('statementAccountNumberReadOnlyHelper') : t('statementAccountNumberHelper')}
                    </small>
                  </Col>
                </Row>
              </div>

              <div className="review-metrics mb-3">
                <div className="review-metric-card review-metric-period">
                  <span className="review-metric-label">{t('statementPeriod')}</span>
                  <span className="review-metric-value">{importPreview.statement.periodLabel}</span>
                </div>
                <div className="review-metric-card review-metric-ready">
                  <span className="review-metric-label">{t('readyToSave')}</span>
                  <span className="review-metric-value">{getReadyToSaveCount(importPreview)}</span>
                </div>
                <div className="review-metric-card review-metric-needs-mapping">
                  <span className="review-metric-label">{t('needsMapping')}</span>
                  <span className="review-metric-value">{getRowsRequiringMappingCount(importPreview)}</span>
                </div>
                <div className="review-metric-card review-metric-skipped">
                  <span className="review-metric-label">{t('willBeSkipped')}</span>
                  <span className="review-metric-value">{getRowsToSkipCount(importPreview)}</span>
                </div>
                <div className="review-metric-card review-metric-ignored">
                  <span className="review-metric-label">{t('ignored')}</span>
                  <span className="review-metric-value">{getIgnoredRowsCount(importPreview)}</span>
                </div>
                <div className="review-metric-card review-metric-invalid">
                  <span className="review-metric-label">{t('invalidRows')}</span>
                  <span className="review-metric-value">{importPreview.invalidRows}</span>
                </div>
                <div className="review-metric-card review-metric-total">
                  <span className="review-metric-label">{t('totalRows')}</span>
                  <span className="review-metric-value">{importPreview.totalRows}</span>
                </div>
              </div>

              <Alert variant="info" className="mb-3 review-overview-alert">
                {t('extractionReviewInfoMessage')}
              </Alert>

              <div className="review-table-wrap position-relative">
                <Table striped bordered hover responsive size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>{t('date')}</th>
                      <th>{t('description')}</th>
                      <th className="text-end">{t('amount')}</th>
                            <th>{isSavingsSelected ? t('systemFeeChargeLabel') : t('accountHolder')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importPreview.transactions.map((tx) => {
                      const normalizedDescription = normalizeDescriptionSpacing(tx.transactionDescription || '');
                      const normalizedRawLine = normalizeDescriptionSpacing(tx.rawLine || '');
                      const inferredChargeCategory = resolveSystemFeeCategory(tx.transactionDescription, systemCategoryKeywordConfig);
                      const mappedSystemCategory = tx.accountHolderName && isSystemFeeCategoryName(tx.accountHolderName)
                        ? tx.accountHolderName
                        : inferredChargeCategory;
                      const isSystemCategoryMapped = tx.isValid && tx.isInStatementPeriod && !tx.isIgnored && !!mappedSystemCategory;
                      const requiresManualMapping = tx.isInStatementPeriod && tx.isValid && !tx.isIgnored && !tx.accountHolderId && !mappedSystemCategory && !tx.alreadyExists;

                      return (
                        <tr
                          key={`${tx.lineNumber}-${tx.rawLine.slice(0, 30)}`}
                          className={!tx.isValid ? 'table-danger' : tx.isIgnored ? 'table-secondary' : requiresManualMapping ? 'table-warning' : ''}
                        >
                          <td>{tx.transactionDate ? formatDate(tx.transactionDate) : '-'}</td>
                          <td>
                            <div className="review-transaction-description">{normalizedDescription || '-'}</div>
                            {!!normalizedRawLine && normalizedRawLine !== normalizedDescription && (
                              <div className="review-raw-line mt-1">{normalizedRawLine}</div>
                            )}
                            {isSystemCategoryMapped && (
                              <small className="d-inline-block mt-1">
                                <span className="badge bg-info text-dark">{t('systemFeeChargeLabel')}: {mappedSystemCategory}</span>
                              </small>
                            )}
                          </td>
                          <td className="text-end">{typeof tx.amount === 'number' ? formatCurrency(tx.amount) : '-'}</td>
                          <td style={{ minWidth: 220 }}>
                            {!isSavingsSelected ? (
                              <div className="app-dropdown-arrow-wrapper">
                                <Form.Select
                                  size="sm"
                                  value={tx.isIgnored ? '__ignore__' : (tx.accountHolderId ?? '')}
                                  onChange={(e) => handleManualMappingChange(tx.lineNumber, e.target.value)}
                                  disabled={!tx.isValid}
                                >
                                  <option value="">{t('selectAccountHolderOption')}</option>
                                  <option value="__ignore__">{t('ignoreThisTransaction')}</option>
                                  {accountHolders.map((holder) => (
                                    <option key={holder.id} value={holder.id}>
                                      {holder.name} ({holder.email})
                                    </option>
                                  ))}
                                </Form.Select>
                                <DropdownArrow />
                              </div>
                            ) : (
                              <div>
                                <div className="app-dropdown-arrow-wrapper">
                                  <Form.Select
                                    size="sm"
                                    value={tx.isIgnored ? '__ignore__' : (mappedSystemCategory || '')}
                                    onChange={(e) => handleSavingsCategoryMappingChange(tx.lineNumber, e.target.value)}
                                    disabled={!tx.isValid}
                                  >
                                    <option value="">{t('selectCategoryOption')}</option>
                                    <option value="__ignore__">{t('ignoreThisTransaction')}</option>
                                    <option value="System Fees">{t('savingsSystemFeesOptionLabel')}</option>
                                    <option value="General">{t('savingsGeneralOptionLabel')}</option>
                                  </Form.Select>
                                  <DropdownArrow />
                                </div>
                                {!tx.isValid && <small className="text-danger d-block mt-1">{t('invalidRow')}: {tx.reason || t('missingRequiredValues')}</small>}
                              </div>
                            )}
                            {tx.isIgnored && tx.isValid && tx.isInStatementPeriod && (
                              <small className="text-muted d-block mt-1">{t('transactionWillBeIgnoredDuringImport')}</small>
                            )}
                            {mappedSystemCategory && tx.isValid && tx.isInStatementPeriod && !tx.isIgnored && (
                              <small className="text-muted d-block mt-1">
                                {tx.accountHolderId
                                  ? `${t('autoMappedToSystemFeeCategory')} ${mappedSystemCategory}.`
                                  : `${t('willAutoCategorizeOnSaveAs')} ${mappedSystemCategory}.`}
                              </small>
                            )}
                            {tx.alreadyExists && tx.isValid && tx.isInStatementPeriod && (
                              <small className="text-muted d-block mt-1">{t('matchingExistingTransactionFoundMessage')}</small>
                            )}
                            {!tx.isValid && !isSavingsSelected && <small className="text-danger d-block mt-1">{t('invalidRow')}: {tx.reason || t('missingRequiredValues')}</small>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>

                {importing && (
                  <div className="review-table-overlay" aria-hidden="true">
                    <span className="dashboard-loading-dots dashboard-loading-dots-compact review-table-overlay-loader">
                      <span className="dashboard-loading-dot" />
                      <span className="dashboard-loading-dot" />
                      <span className="dashboard-loading-dot" />
                    </span>
                    <span className="review-table-overlay-text">{t('savingTransactions')}</span>
                  </div>
                )}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              if (importing) return;
              if (importPreview) {
                setShowImportCloseConfirm(true);
              } else {
                setShowReviewModal(false);
                setIsStatementAccountNumberAutoPopulated(false);
                setIsStatementAccountNumberManualOverride(false);
                setReviewFeedback(null);
                setReviewValidationErrors([]);
              }
            }}
            disabled={importing}
          >
            {t('cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveReviewedImport}
            disabled={importing || !importPreview || getReadyToSaveCount(importPreview) === 0}
          >
            {importing ? (
              <>
                <span className="dashboard-loading-dots dashboard-loading-dots-compact me-2" aria-hidden="true">
                  <span className="dashboard-loading-dot" />
                  <span className="dashboard-loading-dot" />
                  <span className="dashboard-loading-dot" />
                </span>
                {t('savingTransactions')}
              </>
            ) : t('saveStatementTransactions')}
          </Button>
        </Modal.Footer>
      </Modal>
      <ConfirmationModal
        show={showImportCloseConfirm}
        title={t('closeImportConfirmTitle')}
        message={t('closeImportConfirmMessage')}
        confirmText={t('closeImportConfirmButton')}
        variant="warning"
        onConfirm={() => {
          setShowImportCloseConfirm(false);
          setShowReviewModal(false);
          setImportPreview(null);
          setIsStatementAccountNumberAutoPopulated(false);
          setIsStatementAccountNumberManualOverride(false);
          setReviewFeedback(null);
        }}
        onCancel={() => setShowImportCloseConfirm(false)}
      />
    </div>
  );
};

export default Dashboard;
